import React, { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ChevronRight, ChevronLeft, Navigation, MapPin, Users, Filter,
  Loader2, RefreshCw, Maximize2, Route
} from "lucide-react";

// ─── Custom SVG Pin Icons ─────────────────────────────────────────────────────
function makePinIcon(fill, size = 36, approximate = false) {
  const opacity = approximate ? "0.75" : "1";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.4}" viewBox="0 0 36 52" opacity="${opacity}">
      <filter id="shadow" x="-30%" y="-20%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.25"/>
      </filter>
      <path d="M18 2C10.27 2 4 8.27 4 16c0 10.5 14 30 14 30S32 26.5 32 16C32 8.27 25.73 2 18 2z"
        fill="${fill}" stroke="white" stroke-width="2" filter="url(#shadow)"
        ${approximate ? 'stroke-dasharray="4 2"' : ''}/>
      <circle cx="18" cy="16" r="5.5" fill="white" opacity="0.92"/>
    </svg>`;
  return L.divIcon({
    html: svg,
    iconSize: [size, size * 1.4],
    iconAnchor: [size / 2, size * 1.4],
    popupAnchor: [0, -(size * 1.35)],
    tooltipAnchor: [0, -(size * 0.9)],
    className: "",
  });
}

// Project pins are LARGER and more distinct than installer pins
const ICONS = {
  schedule:         makePinIcon("#F97316", 44),
  dispatch:         makePinIcon("#2563EB", 44),       // brighter blue, larger
  installer:        makePinIcon("#16A34A", 30),       // smaller green
  schedule_approx:  makePinIcon("#F97316", 44, true),
  dispatch_approx:  makePinIcon("#2563EB", 44, true),
  installer_approx: makePinIcon("#16A34A", 30, true),
};

// ─── FitBoundsButton (inside MapContainer) ────────────────────────────────────
function FitBoundsControl({ pins }) {
  const map = useMap();
  const handleFit = () => {
    const valid = pins.filter((p) => p.geocode_lat && p.geocode_lng);
    if (!valid.length) return;
    const bounds = L.latLngBounds(valid.map((p) => [p.geocode_lat, p.geocode_lng]));
    map.fitBounds(bounds, { padding: [48, 48] });
  };
  return (
    <button
      data-testid="fit-bounds-btn"
      onClick={handleFit}
      title="Fit map to visible pins"
      className="absolute bottom-6 left-4 z-[1001] bg-white/90 backdrop-blur border border-gray-200 shadow-md
        rounded-lg px-3 py-2 text-xs font-medium text-gray-700 flex items-center gap-1.5 hover:bg-white transition-colors"
    >
      <Maximize2 className="w-3.5 h-3.5" />
      Fit Pins
    </button>
  );
}

// ─── Smooth Zoom-to-pin helper (inside MapContainer) ─────────────────────────
function ZoomToPin({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target?.geocode_lat && target?.geocode_lng) {
      map.flyTo([target.geocode_lat, target.geocode_lng], 14, { duration: 0.8 });
    }
  }, [target, map]);
  return null;
}

// ─── Smart Route Panel ────────────────────────────────────────────────────────
function SmartRoutePanel({ open, projects, onClose, onAssign }) {
  const [selProject, setSelProject] = useState("");
  const [timeWindow, setTimeWindow] = useState("week");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFind = async () => {
    if (!selProject) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/map/smart-route?project_id=${selProject}&window=${timeWindow}`);
      setResults(data);
    } catch {
      toast.error("Failed to load smart route");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (installerId) => {
    try {
      await onAssign(selProject, installerId);
      toast.success("Installer assigned");
      handleFind(); // refresh
    } catch {
      toast.error("Assignment failed");
    }
  };

  return (
    <div
      data-testid="smart-route-panel"
      className={`absolute top-0 right-0 h-full z-[1002] flex transition-all duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      style={{ width: 340 }}
    >
      {/* Toggle arrow */}
      <button
        data-testid="sidebar-toggle-btn"
        onClick={onClose}
        className="absolute -left-8 top-1/2 -translate-y-1/2 w-8 h-16 bg-white/90 backdrop-blur border border-r-0 border-gray-200 shadow-md
          rounded-l-lg flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-white transition-colors z-[1003]"
      >
        {open ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      <div className="w-full bg-white/95 backdrop-blur-md border-l border-gray-200 shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex items-center gap-2">
          <Route className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-800">Smart Route Finder</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Project selector */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Job</label>
            <select
              data-testid="route-project-select"
              value={selProject}
              onChange={(e) => { setSelProject(e.target.value); setResults(null); }}
              className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a job…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.job_number ? ` · #${p.job_number}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Window selector */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">Time Window</label>
            <div className="flex rounded-md overflow-hidden border border-gray-200">
              {["day", "week"].map((w) => (
                <button
                  key={w}
                  data-testid={`window-${w}`}
                  onClick={() => setTimeWindow(w)}
                  className={`flex-1 text-xs py-1.5 font-medium transition-colors ${
                    timeWindow === w
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {w === "day" ? "Today" : "This Week"}
                </button>
              ))}
            </div>
          </div>

          {/* Find Best Match */}
          <button
            data-testid="find-best-match-btn"
            onClick={handleFind}
            disabled={!selProject || loading}
            className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            Find Best Match
          </button>

          {/* Results */}
          {results && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Ranked Matches — {results.results.length}
                </span>
                <span className="text-xs text-gray-400">{results.project?.address}</span>
              </div>

              {results.results.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
                  No qualified installers found
                </div>
              )}

              {results.results.map((r, i) => (
                <div
                  key={r.installer_id}
                  data-testid={`route-result-${i}`}
                  className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 hover:border-blue-200 hover:bg-blue-50/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 truncate">{r.name}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(r.skills || []).slice(0, 4).map((s) => (
                          <span key={s} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      data-testid={`assign-btn-${i}`}
                      onClick={() => handleAssign(r.installer_id)}
                      className="shrink-0 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors"
                    >
                      Assign
                    </button>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-green-500" />
                      {r.dist_miles != null ? `${r.dist_miles} mi` : "No location"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-blue-400" />
                      {r.workload} job{r.workload !== 1 ? "s" : ""} this {timeWindow === "day" ? "day" : "week"}
                    </span>
                    {r.home_region && (
                      <span className="text-gray-400">{r.home_region}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function MapToolbar({
  filters, onFiltersChange, scopeOptions, counts,
  geocodingLeft, failedCount, onRefresh,
}) {
  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeRef = useRef(null);

  useEffect(() => {
    function handle(e) {
      if (scopeRef.current && !scopeRef.current.contains(e.target)) setScopeOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const toggleScope = (code) => {
    const cur = filters.scopes;
    onFiltersChange({
      ...filters,
      scopes: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
    });
  };

  return (
    <div
      data-testid="map-toolbar"
      className="absolute top-4 left-4 right-4 z-[1001] flex flex-wrap items-center gap-2"
    >
      <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-gray-200 shadow-md rounded-xl px-3 py-2">
        {/* Date range */}
        {["today", "week", "month", "all"].map((range) => (
          <button
            key={range}
            data-testid={`date-filter-${range}`}
            onClick={() => onFiltersChange({ ...filters, dateRange: range })}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              filters.dateRange === range
                ? "bg-gray-900 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {range === "today" ? "Today" : range === "week" ? "This Week" : range === "month" ? "This Month" : "All"}
          </button>
        ))}
      </div>

      {/* Scope filter */}
      <div ref={scopeRef} className="relative">
        <button
          data-testid="scope-filter-btn"
          onClick={() => setScopeOpen((v) => !v)}
          className={`flex items-center gap-1.5 bg-white/90 backdrop-blur-md border shadow-md rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
            filters.scopes.length > 0 ? "border-blue-400 text-blue-700 bg-blue-50/90" : "border-gray-200 text-gray-600"
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Scopes {filters.scopes.length > 0 && `(${filters.scopes.length})`}
        </button>
        {scopeOpen && (
          <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 shadow-xl rounded-xl p-2 z-10 min-w-[180px] max-h-56 overflow-y-auto">
            {scopeOptions.map((code) => (
              <label key={code} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={filters.scopes.includes(code)}
                  onChange={() => toggleScope(code)}
                  className="rounded"
                />
                {code}
              </label>
            ))}
            {scopeOptions.length === 0 && <p className="text-xs text-gray-400 p-2">No scopes</p>}
          </div>
        )}
      </div>

      {/* Toggle checkboxes */}
      <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-gray-200 shadow-md rounded-xl px-3 py-2">
        {[
          { key: "showSchedule", label: "Schedule", color: "bg-orange-400" },
          { key: "showDispatch", label: "Dispatch", color: "bg-blue-500" },
          { key: "showInstallers", label: "Installers", color: "bg-green-500" },
        ].map(({ key, label, color }) => (
          <label key={key} className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-700">
            <input
              type="checkbox"
              data-testid={`toggle-${key}`}
              checked={filters[key]}
              onChange={() => onFiltersChange({ ...filters, [key]: !filters[key] })}
              className="sr-only"
            />
            <span className={`w-3 h-3 rounded-full ${color} transition-opacity ${!filters[key] ? "opacity-30" : ""}`} />
            {label}
          </label>
        ))}
      </div>

      {/* Pin count badges */}
      <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-gray-200 shadow-md rounded-xl px-3 py-2 text-xs text-gray-600">
        <span className="font-medium text-blue-600">{counts.dispatch} dispatched</span>
        <span className="text-gray-300">·</span>
        <span className="font-medium text-orange-500">{counts.schedule} scheduled</span>
        <span className="text-gray-300">·</span>
        <span className="font-medium text-green-600">{counts.installers} installers</span>
      </div>

      {/* Geocoding progress / failed warning / refresh */}
      {geocodingLeft > 0 ? (
        <div className="flex items-center gap-2 bg-amber-50/90 backdrop-blur-md border border-amber-200 shadow-md rounded-xl px-3 py-2 text-xs text-amber-700">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Geocoding {geocodingLeft} address{geocodingLeft !== 1 ? "es" : ""}…
        </div>
      ) : (
        <>
          {failedCount > 0 && (
            <div
              data-testid="geocode-failed-badge"
              title="These jobs have incomplete or unrecognized addresses. Edit the job address to fix."
              className="flex items-center gap-1.5 bg-red-50/90 backdrop-blur-md border border-red-200 shadow-md rounded-xl px-3 py-2 text-xs text-red-700 cursor-help"
            >
              <MapPin className="w-3.5 h-3.5" />
              {failedCount} job{failedCount !== 1 ? "s" : ""} not located
            </div>
          )}
          <button
            data-testid="refresh-map-btn"
            onClick={onRefresh}
            title="Refresh map"
            className="bg-white/90 backdrop-blur-md border border-gray-200 shadow-md rounded-xl p-2 text-gray-500 hover:text-gray-800 hover:bg-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main MapView ─────────────────────────────────────────────────────────────
export default function MapView() {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocodingQueue, setGeocodingQueue] = useState([]);
  const [geocodingLeft, setGeocodingLeft] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [zoomTarget, setZoomTarget] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: "all",
    scopes: [],
    showSchedule: true,
    showDispatch: true,
    showInstallers: true,
  });

  // ── Fetch pins ────────────────────────────────────────────────────────────
  const fetchPins = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dateRange !== "all") {
        const today = new Date();
        const fmt = (d) => d.toISOString().slice(0, 10);
        if (filters.dateRange === "today") {
          params.set("start", fmt(today));
          params.set("end", fmt(today));
        } else if (filters.dateRange === "week") {
          const mon = new Date(today);
          mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
          const sun = new Date(mon);
          sun.setDate(mon.getDate() + 6);
          params.set("start", fmt(mon));
          params.set("end", fmt(sun));
        } else if (filters.dateRange === "month") {
          const start = new Date(today.getFullYear(), today.getMonth(), 1);
          const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          params.set("start", fmt(start));
          params.set("end", fmt(end));
        }
      }
      if (filters.scopes.length > 0) params.set("scopes", filters.scopes.join(","));

      const { data } = await api.get(`/map/pins?${params}`);
      setPins(data.pins || []);

      // Queue uncached pins for geocoding
      const toGeocode = (data.pins || []).filter((p) => p.needs_geocode && !p.geocode_lat);
      setGeocodingQueue(toGeocode);
      setGeocodingLeft(toGeocode.length);
    } catch {
      toast.error("Failed to load map data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPins(); }, [fetchPins]);

  // ── Geocoding queue processor (1 req/sec) ─────────────────────────────────
  useEffect(() => {
    if (geocodingQueue.length === 0) return;
    let cancelled = false;

    async function processQueue() {
      for (let i = 0; i < geocodingQueue.length; i++) {
        if (cancelled) break;
        const pin = geocodingQueue[i];
        if (!pin.address) continue;
        try {
          const { data } = await api.post("/map/geocode", {
            entity_type: pin.entity_type,
            entity_id: pin.id,
            address: pin.address,
          });
          setPins((prev) =>
            prev.map((p) =>
              p.id === pin.id
                ? { ...p, geocode_lat: data.lat, geocode_lng: data.lng, needs_geocode: false, geocode_approximate: data.is_approximate }
                : p
            )
          );
        } catch {
          // Mark as permanently failed so the warning badge updates
          setPins((prev) =>
            prev.map((p) => p.id === pin.id ? { ...p, geocode_failed: true } : p)
          );
        } finally {
          setGeocodingLeft((n) => Math.max(0, n - 1));
          await new Promise((res) => setTimeout(res, 1100));
        }
      }
    }

    processQueue();
    return () => { cancelled = true; };
  }, [geocodingQueue]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const visiblePins = pins.filter((p) => {
    if (!p.geocode_lat || !p.geocode_lng) return false;
    if (p.type === "schedule" && !filters.showSchedule) return false;
    if (p.type === "dispatch" && !filters.showDispatch) return false;
    if (p.type === "installer" && !filters.showInstallers) return false;
    return true;
  });

  const scopeOptions = [...new Set(pins.flatMap((p) => p.scope_codes || []))].sort();

  const projectPins = pins.filter((p) => p.entity_type === "project");
  const counts = {
    dispatch: pins.filter((p) => p.type === "dispatch").length,
    schedule: pins.filter((p) => p.type === "schedule").length,
    installers: pins.filter((p) => p.type === "installer").length,
  };
  const failedCount = pins.filter((p) => p.geocode_failed).length;

  const handleAssign = async (projectId, installerId) => {
    await api.post("/map/assign", { project_id: projectId, installer_id: installerId });
    fetchPins();
  };

  // Default center: Denver, CO
  const CENTER = [39.7392, -104.9903];

  return (
    <div data-testid="map-view" className="relative" style={{ height: "calc(100vh - 56px)" }}>
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-[2000] bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white shadow-lg rounded-xl px-6 py-4">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Loading map data…</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <MapToolbar
        filters={filters}
        onFiltersChange={setFilters}
        scopeOptions={scopeOptions}
        counts={counts}
        geocodingLeft={geocodingLeft}
        failedCount={failedCount}
        onRefresh={fetchPins}
      />

      {/* Leaflet Map */}
      <MapContainer
        center={CENTER}
        zoom={10}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <ZoomToPin target={zoomTarget} />

        <FitBoundsControl pins={visiblePins} />

        {visiblePins.map((pin) => {
          const iconKey = pin.geocode_approximate ? `${pin.type}_approx` : pin.type;
          return (
            <Marker
              key={pin.id}
              position={[pin.geocode_lat, pin.geocode_lng]}
              icon={ICONS[iconKey] || ICONS[pin.type] || ICONS.schedule}
              zIndexOffset={pin.entity_type === "project" ? 1000 : 0}
              eventHandlers={{ click: () => setZoomTarget(pin) }}
            >
              {/* Hover tooltip — compact summary */}
              <Tooltip direction="top" offset={[0, -32]} opacity={0.97}>
                <div className="text-xs leading-snug" style={{ maxWidth: 220 }}>
                  {pin.entity_type === "project" ? (
                    <>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          pin.type === "dispatch" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
                        }`}>
                          {pin.type === "dispatch" ? "DISPATCHED" : "SCHEDULED"}
                        </span>
                        {pin.job_number && <span className="text-gray-500 text-[10px]">#{pin.job_number}</span>}
                      </div>
                      <p className="font-semibold text-gray-900">{pin.name}</p>
                      {pin.mob_titles?.length > 0 && (
                        <p className="font-mono text-[11px] text-gray-600 mt-0.5">{pin.mob_titles.slice(0, 4).join("  ·  ")}</p>
                      )}
                      {pin.scope_codes?.length > 0 && !pin.mob_titles?.length && (
                        <p className="text-gray-500 mt-0.5">{pin.scope_codes.join(" · ")}</p>
                      )}
                      {pin.dates?.[0] && <p className="text-gray-400 text-[10px]">{pin.dates[0]}</p>}
                      {pin.geocode_approximate && <p className="text-amber-600 text-[10px]">~ Approximate location</p>}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700">INSTALLER</span>
                      </div>
                      <p className="font-semibold text-gray-900">{pin.name}</p>
                      {pin.home_region && <p className="text-gray-400 text-[10px]">{pin.home_region}</p>}
                      {pin.scope_codes?.length > 0 && (
                        <p className="text-gray-500 mt-0.5">{pin.scope_codes.slice(0, 5).join(" · ")}{pin.scope_codes.length > 5 ? " …" : ""}</p>
                      )}
                    </>
                  )}
                </div>
              </Tooltip>
              {/* Click popup — full detail */}
              <Popup minWidth={240} maxWidth={320}>
                {pin.entity_type === "project" ? (
                  <ProjectPopup pin={pin} />
                ) : (
                  <InstallerPopup pin={pin} />
                )}
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Smart Route Sidebar */}
      <SmartRoutePanel
        open={sidebarOpen}
        projects={projectPins}
        onClose={() => setSidebarOpen((v) => !v)}
        onAssign={handleAssign}
      />
    </div>
  );
}

// ─── Popup sub-components ─────────────────────────────────────────────────────
function ProjectPopup({ pin }) {
  const typeLabel = pin.type === "dispatch" ? "Dispatched" : "Scheduled";
  const typeColor = pin.type === "dispatch" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700";

  return (
    <div className="text-sm space-y-2" style={{ minWidth: 220 }}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900 leading-tight">{pin.name}</p>
          {pin.job_number && <p className="text-xs text-gray-400">#{pin.job_number}</p>}
        </div>
        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full shrink-0 ${typeColor}`}>
          {typeLabel}
        </span>
      </div>
      {pin.address && (
        <p className="text-xs text-gray-500">
          {pin.address}
          {pin.geocode_approximate && (
            <span className="ml-1 text-amber-600 font-medium">(approx. location)</span>
          )}
        </p>
      )}
      {pin.mob_titles?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {pin.mob_titles.map((t) => (
            <span key={t} className="px-1.5 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 font-mono text-[10px] rounded font-semibold">{t}</span>
          ))}
        </div>
      )}
      {pin.scope_codes?.length > 0 && !pin.mob_titles?.length && (
        <div className="flex flex-wrap gap-1">
          {pin.scope_codes.map((s) => (
            <span key={s} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">{s}</span>
          ))}
        </div>
      )}
      {pin.dates?.length > 0 && (
        <p className="text-xs text-gray-500">
          <span className="font-medium">Date: </span>
          {pin.dates[0]}
          {pin.dates.length > 1 && ` + ${pin.dates.length - 1} more`}
        </p>
      )}
      {pin.installer_names?.length > 0 && (
        <div className="border-t border-gray-100 pt-1.5">
          <p className="text-xs font-medium text-gray-500 mb-1">Assigned:</p>
          {pin.installer_names.map((n) => (
            <p key={n} className="text-xs text-gray-700">• {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function InstallerPopup({ pin }) {
  return (
    <div className="text-sm space-y-1.5" style={{ minWidth: 180 }}>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
        <p className="font-semibold text-gray-900">{pin.name}</p>
      </div>
      {pin.home_region && <p className="text-xs text-gray-500">{pin.home_region}</p>}
      {pin.address && <p className="text-xs text-gray-400 truncate">{pin.address}</p>}
      {pin.scope_codes?.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {pin.scope_codes.slice(0, 6).map((s) => (
            <span key={s} className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] rounded border border-green-100">
              {s}
            </span>
          ))}
          {pin.scope_codes.length > 6 && (
            <span className="text-[10px] text-gray-400">+{pin.scope_codes.length - 6}</span>
          )}
        </div>
      )}
    </div>
  );
}
